import React, { useEffect, useMemo } from 'react';
import {
  AlertIcon,
  Backdrop,
  CloseButton,
  Container,
  Content,
  Header,
  IconContainer,
  Title,
  TitleAndIconContainer,
  Wrapper,
} from './Modal.styles';
import { IconClose } from '@components/icons';
import { Loader } from '@components/common/Loader';
import mixpanel from 'mixpanel-browser';

export type ModalTitleIcons = 'alert' | 'warning' | 'success';
interface ModalInterfaceProps {
  title: string;
  children?: React.ReactNode;
  onClose?: () => void;
  withLoader?: boolean;
  titleIcon?: ModalTitleIcons;
  hideCloseButton?: boolean;
}

const Modal = ({ children, title, onClose, withLoader, hideCloseButton, titleIcon }: ModalInterfaceProps) => {
  const handleCloseClick = () => {
    if (!onClose) return;
    onClose();
  };

  const setTitleIcon = () => {
    switch (titleIcon) {
      case 'alert':
        return <AlertIcon />;
    }
  };

  let isLoaded = false;
  useEffect(() => {
    if (!isLoaded) {
      isLoaded = true;
      const name = title.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
      mixpanel.track('Modal opened', { name });
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const headerContent = useMemo(() => {
    if (withLoader) {
      return (
        <>
          <Header>
            {withLoader && <Loader />}
            {!hideCloseButton && (
              <CloseButton onClick={handleCloseClick}>
                <IconClose />
              </CloseButton>
            )}
          </Header>
          <Title withMarginBottom>{title}</Title>
        </>
      );
    }

    if (titleIcon) {
      return (
        <Header>
          <TitleAndIconContainer>
            <IconContainer>{setTitleIcon()}</IconContainer>
            <Title>{title}</Title>
          </TitleAndIconContainer>
          {!hideCloseButton && (
            <CloseButton onClick={handleCloseClick}>
              <IconClose />
            </CloseButton>
          )}
        </Header>
      );
    }

    return (
      <Header>
        <Title>{title}</Title>
        {!hideCloseButton && (
          <CloseButton onClick={handleCloseClick}>
            <IconClose />
          </CloseButton>
        )}
      </Header>
    );
  }, [withLoader, title, hideCloseButton, titleIcon]);

  return (
    <>
      <Backdrop onClick={handleCloseClick} />
      <Container>
        <Wrapper>
          <Content>
            {headerContent}
            {children}
          </Content>
        </Wrapper>
      </Container>
    </>
  );
};

export default Modal;
